/**
 * The stored coverage formula: its AST, its validation, and the brand that
 * proves a model was validated.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THERE IS NO CYCLE DETECTOR. DO NOT ADD ONE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A cycle is not something this AST detects and rejects. A cycle is something
 * this AST CANNOT EXPRESS.
 *
 * `steps` is an ORDERED array. A reference is either an input, or a step
 * identified by its POSITION, and `assertRefIsEarlier` refuses any step
 * reference whose index is not STRICTLY LESS than the referring step's own
 * index. Self-reference fails the same check. The reachable graph is therefore
 * a subgraph of a strict total order, and a strict total order has no cycles —
 * not "no cycles were found", none can be written down.
 *
 * The authoring path is stronger still. `coverage-formula.builder.ts` hands
 * back a handle only for a step it has ALREADY appended, so authoring code
 * cannot even name a later step: a forward reference is unconstructible, not
 * rejected. See that file for where that guarantee stops.
 *
 * This is why the guarantee is worth defending. A cycle detector would move the
 * invariant from "unrepresentable" to "checked by a pass someone must remember
 * to run", and every future version would inherit the possibility of a cycle
 * bug that today cannot exist without a schema change. If you find yourself
 * wanting one, what you actually want is named references or reordering — and
 * either of those needs this comment answered first, in a design document, not
 * worked around here.
 *
 * ── WHY POSITION AND NOT NAME ───────────────────────────────────────────────
 *
 * Named steps read better and are checkable too, but names give no order. The
 * "strictly earlier" test is a comparison of two integers under the array's own
 * ordering; with names it becomes a graph traversal — which is a cycle
 * detector, which is the thing this design exists to not have. `label` carries
 * the human-readable name and is never referenced.
 *
 * ── WHAT IS DELIBERATELY ABSENT FROM THE LANGUAGE ───────────────────────────
 *
 * No conditionals, no loops, no strings, no user-supplied identifiers resolved
 * at evaluation time. `eval`, `new Function`, `vm2` and every third-party
 * parser are out — not as a style preference, but because this JSON is written
 * by a customer's admin and evaluated in the customer's browser AND on the
 * server. It is an untrusted-input boundary, and the only safe expression
 * language is one whose entire vocabulary is a closed enum.
 */
import { z } from "zod";

import {
  COVERAGE_EXPONENT_MAX,
  COVERAGE_EXPONENT_MIN,
  type CoverageDimension,
  type CoverageExponent
} from "./coverage-dimension.types";
import { coverageErr, coverageOk, type CoverageResult } from "./coverage-result.types";
import { COVERAGE_TIME_UNITS } from "./coverage-time-unit.types";

/**
 * The closed function catalog.
 *
 * SIX, not seven. Every candidate for a seventh reduced to one of these or
 * broke a guarantee the engine depends on:
 *
 * - `neg` is `sub` against an authored negative, and an input may already be
 *   negative. Redundant.
 * - `pow` would let a runtime value decide a dimension exponent, which is
 *   exactly the property that makes the dimension check static and checkable.
 * - `convert` is unnecessary: units are canonicalised to hours at input
 *   binding, by exponent, so no in-model conversion exists to express. The
 *   period basis added in `coverage-period.types.ts` did not change this — an
 *   input's `perPeriod` annualises the magnitude at the SAME binding site, by
 *   the same rule, so there is still no in-model conversion to express. A model
 *   that declares its periods does not multiply by a `weeksPerYear` input at
 *   all; the annualisation the design's worked example forgot is carried by the
 *   binding, not by a step.
 * - `clamp` is refused on principle. The design forbids clamping a ratio
 *   because clamping hides the misconfiguration a reader most needs to see,
 *   and shipping a clamp primitive invites exactly that at the model level.
 * - `round` and `abs` destroy information mid-computation and would weaken the
 *   determinism argument for nothing.
 *
 * `min` and `max` earn their place: capping a saving at the hours actually
 * available for a task is a real modelling need, and both are dimension-
 * preserving, total, and exactly specified by ECMA-262.
 */
export const COVERAGE_FUNCTIONS = ["add", "sub", "mul", "div", "min", "max"] as const;

/** One of the six allowed functions. */
export type CoverageFunction = (typeof COVERAGE_FUNCTIONS)[number];

/** How many arguments each function takes. `null` maximum means variadic. */
export const COVERAGE_FUNCTION_ARITY: Readonly<
  Record<CoverageFunction, { readonly min: number; readonly max: number | null }>
> = {
  add: { min: 2, max: null },
  mul: { min: 2, max: null },
  min: { min: 2, max: null },
  max: { min: 2, max: null },
  sub: { min: 2, max: 2 },
  div: { min: 2, max: 2 }
};

/** Upper bounds on model size. A stored model is authored by hand in a UI. */
export const COVERAGE_MAX_INPUTS = 64;

/** Upper bound on the number of steps in one model. */
export const COVERAGE_MAX_STEPS = 128;

/** Upper bound on the arguments of one variadic step. */
export const COVERAGE_MAX_STEP_ARGS = 32;

const ExponentSchema = z.number().int().min(COVERAGE_EXPONENT_MIN).max(COVERAGE_EXPONENT_MAX);

/** A dimension as it is stored: four integer exponents. */
export const CoverageDimensionSchema = z.strictObject({
  time: ExponentSchema,
  person: ExponentSchema,
  event: ExponentSchema,
  money: ExponentSchema
});

/**
 * An input key: a stable identifier, never a display string.
 *
 * Constrained so a key can never be confused with a label, can be used as an
 * object key without escaping, and cannot collide across casing conventions.
 */
export const CoverageInputKeySchema = z.string().regex(/^[a-z][a-zA-Z0-9_]{0,63}$/, {
  message: "input key must start with a lowercase letter, then letters, digits or underscores"
});

const LabelSchema = z.string().min(1).max(160);

const FiniteNumberSchema = z
  .number()
  .refine((n) => Number.isFinite(n), { message: "must be a finite number" });

/**
 * One authored input: a magnitude, its dimension, and the unit its magnitude is
 * stated in.
 *
 * The VALUE lives in the model, not in the context. The model is the thing the
 * admin authored — "12 people", "35 hours a week" — and it must reproduce the
 * same number a year from now regardless of what the caller passes. The context
 * carries only the organisation's working-time model and, later, measured
 * bindings that override an estimate.
 */
export const CoverageInputSchema = z
  .strictObject({
    key: CoverageInputKeySchema,
    value: FiniteNumberSchema,
    dimension: CoverageDimensionSchema,
    timeUnit: z.enum(COVERAGE_TIME_UNITS).optional(),
    /**
     * The accumulation WINDOW this magnitude is counted over — `40 tickets per
     * WEEK`. Absent means the magnitude is absolute: a stock, a factor, or a
     * rate per work item.
     *
     * Distinct from `timeUnit`, and the distinction is the point. `timeUnit`
     * says what unit the magnitude's own TIME dimension is stated in (12
     * MINUTES per ticket); `perPeriod` says over what span it accumulates (40
     * tickets per WEEK). Both are time units and they mean different things, so
     * `35 hours per week` carries `timeUnit: "hour"` and `perPeriod: "week"`.
     * Conflating them is exactly the error this axis exists to catch — see
     * `coverage-period.types.ts`.
     */
    perPeriod: z.enum(COVERAGE_TIME_UNITS).optional(),
    label: LabelSchema.optional()
  })
  .superRefine((input, ctx) => {
    // A unit on a quantity with no TIME exponent scales by `factor ^ 0 === 1`,
    // so it would read as meaningful and do nothing. Unrepresentable is better
    // than inert.
    if (input.dimension.time === 0 && input.timeUnit !== undefined && input.timeUnit !== "hour") {
      ctx.addIssue({
        code: "custom",
        path: ["timeUnit"],
        message: `input "${input.key}" has no time dimension, so a time unit would have no effect`
      });
    }
  });

/**
 * A reference to a value already available at this point in the computation.
 *
 * Exactly two kinds, and neither can name something that does not yet exist —
 * see the file header.
 */
export const CoverageRefSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("input"), key: CoverageInputKeySchema }),
  z.strictObject({ kind: z.literal("step"), index: z.number().int().min(0) })
]);

/** One step: a function applied to earlier values. */
export const CoverageStepSchema = z.strictObject({
  fn: z.enum(COVERAGE_FUNCTIONS),
  args: z.array(CoverageRefSchema).min(2).max(COVERAGE_MAX_STEP_ARGS),
  label: LabelSchema.optional()
});

/** The stored formula. `result` names the value the model produces. */
export const CoverageFormulaSchema = z
  .strictObject({
    version: z.literal(1),
    inputs: z.array(CoverageInputSchema).max(COVERAGE_MAX_INPUTS),
    steps: z.array(CoverageStepSchema).max(COVERAGE_MAX_STEPS),
    result: CoverageRefSchema
  })
  .superRefine(applyCoverageFormulaRefinements);

/** A dimension exactly as stored. Structurally a {@link CoverageDimension}. */
export type CoverageStoredDimension = z.infer<typeof CoverageDimensionSchema>;

/** One authored input, exactly as stored. */
export type CoverageInput = z.infer<typeof CoverageInputSchema>;

/** A reference to an input or to a strictly earlier step. */
export type CoverageRef = z.infer<typeof CoverageRefSchema>;

/** One step of the computation. */
export type CoverageStep = z.infer<typeof CoverageStepSchema>;

/** A coverage formula as it is stored, before validation. */
export type CoverageFormula = z.infer<typeof CoverageFormulaSchema>;

/**
 * The proof that a formula passed {@link CoverageFormulaSchema}.
 *
 * `evaluateCoverageFormula` accepts nothing else, so "did anyone validate this
 * JSON?" is answered by the compiler rather than by reading call sites. The
 * brand is phantom — it costs nothing at runtime and never reaches the stored
 * JSON.
 */
export interface CoverageFormulaValidationBrand<B extends CoverageExponent = CoverageExponent> {
  readonly __coverageFormulaValidated: true;
  /**
   * The PERIOD BASIS of the value this formula produces, when it is known.
   *
   * Phantom: declared, never assigned, never read, and absent from the stored
   * JSON. It exists so `computeRoleCoverage` can refuse a per-year denominator
   * under an absolute numerator at COMPILE time — see `coverage-ratio.ts`.
   *
   * `buildCoverageFormula` fills it with the literal basis of the term the
   * callback returned. `parseCoverageFormula` leaves it as the full
   * `CoverageExponent` union, which is the honest statement that stored JSON
   * carries no compile-time basis and the runtime check governs there.
   */
  readonly __coverageResultPeriodBasis?: B;
}

/**
 * A formula that provably passed validation. The only thing the evaluator accepts.
 *
 * The type parameter is the period basis of its result. It defaults to the
 * whole exponent union — "basis unknown" — so every existing annotation of the
 * bare `ValidatedCoverageFormula` keeps its meaning, and a formula with a known
 * basis widens into one by ordinary assignment, with no cast. That widening is
 * the sanctioned way to give up the compile-time check deliberately; it is
 * sound because it drops a guarantee rather than asserting a false one.
 */
export type ValidatedCoverageFormula<B extends CoverageExponent = CoverageExponent> =
  CoverageFormula & CoverageFormulaValidationBrand<B>;

/** Why a candidate formula was refused. */
export interface CoverageFormulaParseFailure {
  readonly code: "INVALID_FORMULA";
  readonly message: string;
  /** One entry per violated rule, each naming its own path inside the model. */
  readonly issues: readonly { readonly path: string; readonly message: string }[];
}

/**
 * Parse untrusted JSON into a formula the evaluator will accept.
 *
 * The ONLY door. Returns a failure value rather than throwing: this runs on a
 * stored row that may predate the current schema, and an exception crossing a
 * render boundary cannot degrade into a named warning the way a value can.
 */
export function parseCoverageFormula(
  candidate: unknown
): CoverageResult<ValidatedCoverageFormula, CoverageFormulaParseFailure> {
  const parsed = CoverageFormulaSchema.safeParse(candidate);
  if (!parsed.success) {
    return coverageErr({
      code: "INVALID_FORMULA",
      message: "coverage formula failed validation",
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message
      }))
    });
  }
  // The single brand-minting cast in this module. `parsed.data` came out of
  // `CoverageFormulaSchema`, which IS the definition of validated, so the cast
  // asserts exactly what the line above proved. It is confined here so no other
  // file can mint the brand.
  return coverageOk(parsed.data as ValidatedCoverageFormula);
}

/**
 * Label a validated formula with the period basis of its result.
 *
 * The SECOND and last narrowing of the validation brand, and it lives here
 * beside the first so no other file can label a formula with a basis. Only
 * `coverage-formula.builder.ts` calls it, and only with the basis it computed
 * itself while appending the steps — the identical value the evaluator derives
 * from the same stored `perPeriod` fields, which
 * `coverage-formula.builder.test.ts` pins against each other.
 *
 * It refuses to label anything else: hand it stored JSON and there is no
 * literal basis to pass, because a parsed formula's basis is only known once it
 * has been evaluated. That is the boundary, and it is why `parseCoverageFormula`
 * returns the unlabelled form.
 */
export function withCoverageResultPeriodBasis<B extends CoverageExponent>(
  formula: ValidatedCoverageFormula
): ValidatedCoverageFormula<B> {
  // The phantom is declared, never assigned and absent from the JSON, so this
  // re-labels a type and moves no bytes. There is deliberately no runtime
  // argument to check: the value that would be checked is the one the caller
  // computed, so a guard here would compare a number against itself. What
  // actually keeps the label honest is the evaluator deriving the SAME basis
  // from the same stored `perPeriod` fields, and the test that pins the two.
  return formula as ValidatedCoverageFormula<B>;
}

/**
 * Strip the validation brand for storage.
 *
 * The brand is phantom, so this is an identity at runtime; it exists so a
 * caller writing the model back to the database says so in the types instead of
 * widening by accident.
 */
export function toStoredCoverageFormula(formula: ValidatedCoverageFormula): CoverageFormula {
  return formula;
}

/** Structural read of a stored dimension as the runtime algebra sees it. */
export function toCoverageDimension(stored: CoverageStoredDimension): CoverageDimension {
  return stored;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Cross-field rules. Everything a single field cannot check on its own.
 * ──────────────────────────────────────────────────────────────────────────── */

function applyCoverageFormulaRefinements(
  model: {
    readonly inputs: readonly CoverageInput[];
    readonly steps: readonly CoverageStep[];
    readonly result: CoverageRef;
  },
  ctx: z.RefinementCtx
): void {
  const declaredKeys = new Set<string>();
  model.inputs.forEach((input, index) => {
    if (declaredKeys.has(input.key)) {
      ctx.addIssue({
        code: "custom",
        path: ["inputs", index, "key"],
        message: `duplicate input key "${input.key}"`
      });
    }
    declaredKeys.add(input.key);
  });

  model.steps.forEach((step, stepIndex) => {
    const arity = COVERAGE_FUNCTION_ARITY[step.fn];
    if (step.args.length < arity.min || (arity.max !== null && step.args.length > arity.max)) {
      ctx.addIssue({
        code: "custom",
        path: ["steps", stepIndex, "args"],
        message:
          arity.max === null
            ? `"${step.fn}" takes at least ${arity.min} arguments, got ${step.args.length}`
            : `"${step.fn}" takes exactly ${arity.max} arguments, got ${step.args.length}`
      });
    }

    step.args.forEach((ref, argIndex) => {
      assertRefResolves(ref, declaredKeys, stepIndex, ctx, ["steps", stepIndex, "args", argIndex]);
    });
  });

  // The result may name any step, so its horizon is the whole array.
  assertRefResolves(model.result, declaredKeys, model.steps.length, ctx, ["result"]);
}

/**
 * A reference resolves when it names a declared input, or a step STRICTLY
 * EARLIER than `horizon`.
 *
 * `horizon` is the referring step's own index — so a step can never name itself
 * and can never name a later step. This one comparison is the entire cycle
 * argument; see the file header before changing it.
 */
function assertRefResolves(
  ref: CoverageRef,
  declaredKeys: ReadonlySet<string>,
  horizon: number,
  ctx: z.RefinementCtx,
  path: (string | number)[]
): void {
  if (ref.kind === "input") {
    if (!declaredKeys.has(ref.key)) {
      ctx.addIssue({
        code: "custom",
        path,
        message: `reference to undeclared input "${ref.key}"`
      });
    }
    return;
  }

  if (ref.index >= horizon) {
    ctx.addIssue({
      code: "custom",
      path,
      message:
        `step reference ${ref.index} is not strictly earlier than position ${horizon} — ` +
        "a coverage formula is an ordered list and a forward or self reference is not expressible"
    });
  }
}
